import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ChevronDown } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { isValidString } from '../../../common/CommonUtilsMethods';
import {
  ListAgencyAttributesAPICall,
  SearchWorkersByAttributesAPICall,
} from '../api/AgencyAttributesAPI';
import {
  AgencyAttributeDTO,
  AttributeAnswerType,
  AttributeStatus,
  WorkerAttributeSearchFilterReqDTO,
  WorkerAttributeSearchResultDTO,
} from '../interfaces/AgencyAttributesInterfaces';
import {
  BuildSearchFiltersInput,
  FilterSummaryInput,
  ToggleSelectedOptionInput,
  UpdateRatingBoundInput,
  WorkforceAttributeFilterSelections,
  WorkforceAttributeSearchFilterProps,
} from './WorkforceAttributeSearchFilterInterfaces';

const FILTER_BY_ATTRIBUTE_PLACEHOLDER: string = 'Filter by Attribute';
const WORKFORCE_ATTRIBUTE_FILTER_TOOLTIP: string =
  'Show only workers whose saved answers match every attribute you choose. If you pick more than one answer on the same attribute, a worker still matches if they have any of those answers.';

const EMPTY_FILTER_SELECTIONS: WorkforceAttributeFilterSelections = {
  selectedOptionIdsByAttributeId: {},
  ratingMinByAttributeId: {},
  ratingMaxByAttributeId: {},
};

const getActiveAttributes = (
  attributes: readonly AgencyAttributeDTO[],
): readonly AgencyAttributeDTO[] =>
  attributes.filter(
    (attribute: AgencyAttributeDTO): boolean => attribute.status === AttributeStatus.ACTIVE,
  );

const isOptionAttribute = (answerType: AttributeAnswerType): boolean =>
  answerType === AttributeAnswerType.SINGLE_SELECT
  || answerType === AttributeAnswerType.MULTI_SELECT;

const parseRatingBound = (ratingBound: string | undefined): number | null => {
  if (!isValidString(ratingBound) || ratingBound === undefined) {
    return null;
  }
  const parsedValue: number = Number(ratingBound);
  if (Number.isNaN(parsedValue)) {
    return null;
  }
  return parsedValue;
};

const toggleSelectedOption = (
  input: ToggleSelectedOptionInput,
): Readonly<Record<string, readonly string[]>> => {
  const currentSelectedOptionIds: readonly string[] =
    input.selectedOptionIdsByAttributeId[input.attributeId] ?? [];
  const nextSelectedOptionIds: readonly string[] = currentSelectedOptionIds.includes(input.optionId)
    ? currentSelectedOptionIds.filter((optionId: string): boolean => optionId !== input.optionId)
    : [...currentSelectedOptionIds, input.optionId];
  return {
    ...input.selectedOptionIdsByAttributeId,
    [input.attributeId]: nextSelectedOptionIds,
  };
};

const updateRatingBound = (
  input: UpdateRatingBoundInput,
): Readonly<Record<string, string>> => ({
  ...input.ratingBoundByAttributeId,
  [input.attributeId]: input.nextValue,
});

const buildSearchFilters = (
  input: BuildSearchFiltersInput,
): readonly WorkerAttributeSearchFilterReqDTO[] => {
  const searchFilters: WorkerAttributeSearchFilterReqDTO[] = [];
  for (const attribute of input.attributes) {
    if (attribute.answerType === AttributeAnswerType.RATING) {
      const ratingMin: number | null = parseRatingBound(
        input.selections.ratingMinByAttributeId[attribute.attributeId],
      );
      const ratingMax: number | null = parseRatingBound(
        input.selections.ratingMaxByAttributeId[attribute.attributeId],
      );
      if (ratingMin === null && ratingMax === null) {
        continue;
      }
      searchFilters.push({
        attributeId: attribute.attributeId,
        ratingMin,
        ratingMax,
      });
      continue;
    }

    if (!isOptionAttribute(attribute.answerType)) {
      continue;
    }

    const selectedOptionIds: readonly string[] =
      input.selections.selectedOptionIdsByAttributeId[attribute.attributeId] ?? [];
    if (selectedOptionIds.length < 1) {
      continue;
    }
    searchFilters.push({
      attributeId: attribute.attributeId,
      selectedOptionIds,
    });
  }
  return searchFilters;
};

const hasActiveAttributeFilters = (
  selections: WorkforceAttributeFilterSelections,
  attributes: readonly AgencyAttributeDTO[],
): boolean =>
  buildSearchFilters({
    attributes,
    selections,
  }).length > 0;

const getFilterSummary = (input: FilterSummaryInput): string => {
  const summaries: string[] = [];
  for (const attribute of input.attributes) {
    if (attribute.answerType === AttributeAnswerType.RATING) {
      const ratingMin: string = input.selections.ratingMinByAttributeId[attribute.attributeId] ?? '';
      const ratingMax: string = input.selections.ratingMaxByAttributeId[attribute.attributeId] ?? '';
      if (!isValidString(ratingMin) && !isValidString(ratingMax)) {
        continue;
      }
      const minLabel: string = isValidString(ratingMin) ? ratingMin : 'any';
      const maxLabel: string = isValidString(ratingMax) ? ratingMax : 'any';
      summaries.push(`${attribute.displayName}: ${minLabel}–${maxLabel}`);
      continue;
    }

    const selectedOptionIds: readonly string[] =
      input.selections.selectedOptionIdsByAttributeId[attribute.attributeId] ?? [];
    if (selectedOptionIds.length < 1) {
      continue;
    }
    const selectedOptionLabels: readonly string[] = selectedOptionIds.map(
      (optionId: string): string =>
        attribute.selectOptions.find(
          (option): boolean => option.optionId === optionId,
        )?.optionLabel ?? optionId,
    );
    summaries.push(`${attribute.displayName}: ${selectedOptionLabels.join(', ')}`);
  }
  if (summaries.length < 1) {
    return FILTER_BY_ATTRIBUTE_PLACEHOLDER;
  }
  return summaries.join('; ');
};

const resolveMatchingWorkerEmails = (
  searchResults: readonly WorkerAttributeSearchResultDTO[],
): readonly string[] => {
  const matchingWorkerEmails: string[] = [];
  for (const searchResult of searchResults) {
    const workerEmail: string | undefined =
      searchResult.workforceWorker?.jobberDetails?.userDetails?.userEmail?.value;
    if (!isValidString(workerEmail) || workerEmail === undefined) {
      continue;
    }
    matchingWorkerEmails.push(workerEmail);
  }
  return matchingWorkerEmails;
};

const WorkforceAttributeSearchFilter = (
  props: WorkforceAttributeSearchFilterProps,
): JSX.Element => {
  const { isMobile, onMatchingWorkerEmailsChange } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [attributes, setAttributes] = useState<readonly AgencyAttributeDTO[]>([]);
  const [isLoadingAttributes, setIsLoadingAttributes] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selections, setSelections] = useState<WorkforceAttributeFilterSelections>(
    EMPTY_FILTER_SELECTIONS,
  );

  useEffect((): void => {
    const loadAttributes = async (): Promise<void> => {
      setIsLoadingAttributes(true);
      const listedAttributes: readonly AgencyAttributeDTO[] | null =
        await ListAgencyAttributesAPICall(false);
      setIsLoadingAttributes(false);
      if (listedAttributes === null) {
        enqueueSnackbar('Unable to load worker attributes for search.', { variant: 'error' });
        setAttributes([]);
        return;
      }
      setAttributes(getActiveAttributes(listedAttributes));
    };
    void loadAttributes();
  }, [enqueueSnackbar]);

  useEffect((): void => {
    const searchWorkersBySelectedAttributes = async (): Promise<void> => {
      const attributeFilters: readonly WorkerAttributeSearchFilterReqDTO[] = buildSearchFilters({
        attributes,
        selections,
      });
      if (attributeFilters.length < 1) {
        onMatchingWorkerEmailsChange(null);
        return;
      }
      setIsSearching(true);
      const searchResults: readonly WorkerAttributeSearchResultDTO[] | null =
        await SearchWorkersByAttributesAPICall({
          attributeFilters,
        });
      setIsSearching(false);
      if (searchResults === null) {
        enqueueSnackbar('Failed to filter workers by attributes. Please try again.', {
          variant: 'error',
        });
        return;
      }
      onMatchingWorkerEmailsChange(resolveMatchingWorkerEmails(searchResults));
    };
    void searchWorkersBySelectedAttributes();
  }, [attributes, enqueueSnackbar, onMatchingWorkerEmailsChange, selections]);

  const isFilterOpen: boolean = anchorEl !== null;
  const summaryLabel: string = getFilterSummary({
    attributes,
    selections,
  });
  const hasFilters: boolean = hasActiveAttributeFilters(selections, attributes);

  const handleOpenFilter = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = (): void => {
    setAnchorEl(null);
  };

  const handleToggleOption = (attributeId: string, optionId: string): void => {
    setSelections((currentSelections: WorkforceAttributeFilterSelections): WorkforceAttributeFilterSelections => ({
      ...currentSelections,
      selectedOptionIdsByAttributeId: toggleSelectedOption({
        selectedOptionIdsByAttributeId: currentSelections.selectedOptionIdsByAttributeId,
        attributeId,
        optionId,
      }),
    }));
  };

  const handleRatingBoundChange = (
    attributeId: string,
    boundKey: 'ratingMinByAttributeId' | 'ratingMaxByAttributeId',
    nextValue: string,
  ): void => {
    setSelections((currentSelections: WorkforceAttributeFilterSelections): WorkforceAttributeFilterSelections => ({
      ...currentSelections,
      [boundKey]: updateRatingBound({
        ratingBoundByAttributeId: currentSelections[boundKey],
        attributeId,
        nextValue,
      }),
    }));
  };

  const handleClearFilters = (): void => {
    setSelections(EMPTY_FILTER_SELECTIONS);
    onMatchingWorkerEmailsChange(null);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: isMobile ? 1 : undefined }}>
      <Tooltip
        title={WORKFORCE_ATTRIBUTE_FILTER_TOOLTIP}
        arrow
        placement="top"
        enterDelay={400}
        leaveDelay={0}
        disableFocusListener
        disableTouchListener
        disableHoverListener={isFilterOpen}
        PopperProps={{
          modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
          sx: {
            '& .MuiTooltip-tooltip': {
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 600,
              fontSize: '13px',
              maxWidth: 280,
            },
          },
        }}
      >
        <button
          type="button"
          onClick={handleOpenFilter}
          style={{
            height: isMobile ? '42px' : '50px',
            minWidth: isMobile ? '140px' : '200px',
            maxWidth: isMobile ? undefined : '300px',
            flex: isMobile ? 1 : undefined,
            borderRadius: isMobile ? '8px' : '15px',
            border: '1px solid #e5e7eb',
            background: 'white',
            padding: '0 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            cursor: 'pointer',
            fontSize: isMobile ? '14px' : '13.5px',
            fontWeight: 500,
            color: hasFilters ? '#374151' : '#9ca3af',
            fontFamily: 'inherit',
          }}
        >
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'left',
            }}
          >
            {summaryLabel}
          </span>
          {isSearching ? <CircularProgress size={14} /> : <ChevronDown size={16} color="#9ca3af" />}
        </button>
      </Tooltip>
      <Popover
        open={isFilterOpen}
        anchorEl={anchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          style: {
            width: isMobile ? 280 : 320,
            maxHeight: 420,
            marginTop: 8,
            borderRadius: 12,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            padding: '8px 0',
          },
        }}
      >
        <div style={{ padding: '8px 16px 12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#111827' }}>
            Filter by attribute answers
          </Typography>
        </div>
        {isLoadingAttributes ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <CircularProgress size={22} />
          </div>
        ) : attributes.length < 1 ? (
          <Typography sx={{ fontSize: '13.5px', color: '#9ca3af', padding: '8px 16px 16px' }}>
            No attributes available
          </Typography>
        ) : (
          attributes.map((attribute: AgencyAttributeDTO): JSX.Element => (
            <Accordion
              key={attribute.attributeId}
              disableGutters
              elevation={0}
              sx={{
                '&:before': { display: 'none' },
                boxShadow: 'none',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40 }}>
                <Typography sx={{ fontSize: '13.5px', fontWeight: 600 }}>
                  {attribute.displayName}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ paddingTop: 0 }}>
                {attribute.answerType === AttributeAnswerType.RATING ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <TextField
                      size="small"
                      label="Min"
                      type="number"
                      value={selections.ratingMinByAttributeId[attribute.attributeId] ?? ''}
                      onChange={(event): void =>
                        handleRatingBoundChange(
                          attribute.attributeId,
                          'ratingMinByAttributeId',
                          event.target.value,
                        )
                      }
                    />
                    <TextField
                      size="small"
                      label="Max"
                      type="number"
                      value={selections.ratingMaxByAttributeId[attribute.attributeId] ?? ''}
                      onChange={(event): void =>
                        handleRatingBoundChange(
                          attribute.attributeId,
                          'ratingMaxByAttributeId',
                          event.target.value,
                        )
                      }
                    />
                  </div>
                ) : attribute.selectOptions.length < 1 ? (
                  <Typography sx={{ fontSize: '13px', color: '#9ca3af' }}>
                    No answers configured
                  </Typography>
                ) : (
                  attribute.selectOptions.map((option): JSX.Element => (
                    <FormControlLabel
                      key={option.optionId}
                      control={
                        <Checkbox
                          size="small"
                          checked={(
                            selections.selectedOptionIdsByAttributeId[attribute.attributeId] ?? []
                          ).includes(option.optionId)}
                          onChange={(): void =>
                            handleToggleOption(attribute.attributeId, option.optionId)
                          }
                          sx={{
                            color: '#d1d5db',
                            '&.Mui-checked': {
                              color: '#2c68bd',
                            },
                          }}
                        />
                      }
                      label={option.optionLabel}
                      sx={{
                        display: 'flex',
                        marginLeft: 0,
                        '& .MuiFormControlLabel-label': {
                          fontSize: '13.5px',
                          fontWeight: 500,
                        },
                      }}
                    />
                  ))
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}
        {hasFilters ? (
          <button
            type="button"
            onClick={handleClearFilters}
            style={{
              background: 'none',
              border: 'none',
              color: '#2c68bd',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              padding: '8px 16px 12px',
            }}
          >
            Clear attribute filters
          </button>
        ) : null}
      </Popover>
    </div>
  );
};

export { WorkforceAttributeSearchFilter };
